
import React from 'react';
import { Settings, Lightbulb } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const BasicsTab = () => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium flex items-center">
        <Settings className="mr-2 h-5 w-5" /> How the Simulation Works
      </h3>
      <p>
        The Bitcoin Mining Career Simulator is a role-playing experience that guides you through
        a career in the Bitcoin mining industry. You'll start in an entry-level position and
        work your way up to leadership roles by making strategic decisions, acquiring skills,
        and improving key performance indicators (KPIs).
      </p>
      
      <div className="border rounded-md p-4 bg-muted/20">
        <h4 className="font-medium mb-2">Simulation Flow</h4>
        <ol className="list-decimal list-inside space-y-2 text-sm">
          <li>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-medium cursor-help">Choose a starting role</span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="w-[200px]">Each role has unique starting skills and career objectives</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            &nbsp;that aligns with your interests
          </li>
          <li>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-medium cursor-help">Advance time</span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="w-[200px]">Each year brings new challenges and opportunities</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            &nbsp;to progress through the simulation years
          </li>
          <li>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-medium cursor-help">Face challenges</span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="w-[200px]">Industry-specific scenarios that test your decision-making</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            &nbsp;that require strategic decisions
          </li>
          <li>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-medium cursor-help">Acquire skills</span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="w-[200px]">Skills provide permanent boosts to your performance metrics</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            &nbsp;through successful challenge outcomes
          </li>
          <li>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-medium cursor-help">Improve KPIs</span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="w-[200px]">Key performance indicators determine your overall success</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            &nbsp;to meet career objectives
          </li>
          <li>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-medium cursor-help">Get promoted</span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="w-[200px]">Promotions unlock new challenges and career paths</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            &nbsp;when you meet time and experience requirements
          </li>
          <li>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-medium cursor-help">Reach leadership</span>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="w-[200px]">Becoming an industry leader is your ultimate goal</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            &nbsp;to complete the simulation
          </li>
        </ol>
      </div>
      
      <div className="border-l-4 border-primary p-4 bg-primary/5">
        <h4 className="font-medium flex items-center">
          <Lightbulb className="mr-2 h-4 w-4 text-primary" /> Tip
        </h4>
        <p className="text-sm">
          The decisions you make during challenges will shape your career path.
          Different options provide different benefits, so choose strategically
          based on your career goals!
        </p>
      </div>
    </div>
  );
};

export default BasicsTab;
